// HeaderControls.tsx
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Button, Drawer, Slider, Space, Typography, Card, Badge, Avatar } from 'antd';
import { 
  SoundOutlined, 
  GlobalOutlined, 
  QuestionCircleOutlined, 
  CloseOutlined, 
  SettingOutlined, 
  ArrowLeftOutlined,
  CheckOutlined,
  SoundFilled,
  CustomerServiceOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Documentation from '../Documentation/Documentation';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { sanitizeAndStore, getAndSanitize } from '../../shared/utils/sanitization';
import { useNotificationCoordinator } from '../../contexts/NotificationContext';

const { Title, Text } = Typography;

interface HeaderControlsProps {
  isMobile: boolean;
}

// Use a portal to render mobile controls directly to body
const MobileMenuPortal: React.FC<{
  children: React.ReactNode;
  isOpen: boolean;
}> = ({ children, isOpen }) => {
  if (!isOpen) return null;
  return ReactDOM.createPortal(children, document.body);
};

const HeaderControls: React.FC<HeaderControlsProps> = ({ isMobile }) => {
  const { i18n, t } = useTranslation(['welcomeModal', 'floatingControls']);
  const { theme } = useTheme();
  const { setDocsNotificationVisible } = useNotificationCoordinator();
  const [soundOpen, setSoundOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentScreen, setCurrentScreen] = useState<'menu' | 'sound' | 'language' | 'docs'>('menu');
  const [docButtonVibrate] = useState(false);
  const [animateSoundButton, setAnimateSoundButton] = useState(false);
  //const [pendingTickets, setPendingTickets] = useState(0);
  
  // Load saved sound settings
  const getSavedVolumes = () => {
    const savedVolumes = getAndSanitize('soundVolumes');
    return savedVolumes ? JSON.parse(savedVolumes) : {
      loadRequest: 100,
      loadApproved: 100,
      loadRejected: 100,
      requiresValidation: 100,
      withdrawRequest: 100,
      userCreated: 100
    };
  };

  const [volumes, setVolumes] = useState(getSavedVolumes());

  // Listen for external open-documentation events (from sidebar banner)
  useEffect(() => {
    const handler = () => setDocsOpen(true);
    window.addEventListener('open-documentation', handler);
    return () => window.removeEventListener('open-documentation', handler);
  }, []);

  // Effect para ocultar la notificación de documentación cuando se abre la documentación
  useEffect(() => {
    if (docsOpen) {
      setDocsNotificationVisible(false);
    }
  }, [docsOpen, setDocsNotificationVisible]);


  // Handle language change
  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    sanitizeAndStore('appLanguage', lang);
  };

  // Handle volume change
  const handleVolumeChange = (value: number, sound: string) => {
    setAnimateSoundButton(true);
    setTimeout(() => setAnimateSoundButton(false), 800);
    
    const newVolumes = {
      ...volumes,
      [sound]: value
    };
    
    setVolumes(newVolumes);
    sanitizeAndStore('soundVolumes', JSON.stringify(newVolumes));
  };

  // Function to handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      if (currentScreen !== 'menu') {
        setCurrentScreen('menu');
      } else {
        setMobileMenuOpen(false);
      }
    }
  };

  // Function to go to a specific screen
  const goToScreen = (screen: 'sound' | 'language' | 'docs') => {
    if (screen === 'docs') {
      setMobileMenuOpen(false);
      setDocsOpen(true);
    } else {
      setCurrentScreen(screen);
    }
  };

  // Function to go back to main menu
  const goBackToMenu = () => {
    setCurrentScreen('menu');
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.8 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        type: "spring", 
        stiffness: 400, 
        damping: 25 
      }
    }
  };

  // Base card style for menu items
  const baseCardStyle = {
    marginBottom: 12,
    borderRadius: 12,
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    background: theme === 'dark' ? 'linear-gradient(145deg, #222, #333)' : 'linear-gradient(145deg, #fff, #f5f5f5)',
    border: `1px solid ${theme === 'dark' ? '#444' : '#eaeaea'}`,
    overflow: 'hidden'
  };

  // Sound categories with enhanced styling
  const soundCategories = [
    {
      key: 'loadRequest',
              label: t('soundLabels.loadRequest', 'Solicitud de carga'),
      color: '#1890ff',
      icon: <SoundOutlined />,
      hoverColor: '#40a9ff'
    },
    {
      key: 'loadApproved',
              label: t('soundLabels.loadApproved', 'Carga aprobada'),
      color: '#52c41a',
      icon: <CheckOutlined />,
      hoverColor: '#73d13d'
    },
    {
      key: 'loadRejected',
              label: t('soundLabels.loadRejected', 'Carga rechazada'),
      color: '#f5222d',
      icon: <CloseOutlined />,
      hoverColor: '#ff4d4f'
    },
    {
      key: 'requiresValidation',
      label: t('soundLabels.requiresValidation', 'Requiere validación'),
      color: '#faad14',
      icon: <QuestionCircleOutlined />,
      hoverColor: '#ffc53d'
    },
    {
      key: 'withdrawRequest',
      label: t('soundLabels.withdrawRequest', 'Solicitud de retiro'),
      color: '#722ed1',
      icon: <ArrowLeftOutlined />,
      hoverColor: '#9254de'
    },
    {
      key: 'userCreated',
      label: t('soundLabels.userCreated', 'Usuario creado'),
      color: '#13c2c2',
      icon: <CustomerServiceOutlined />,
      hoverColor: '#36cfc9'
    }
  ];

  // Language options with flags
  const languageOptions = [
    {
      value: 'en',
      label: 'English',
      flag: '🇺🇸',
      color: '#2B67C9',
      description: 'American English'
    },
    {
      value: 'es',
      label: 'Español',
      flag: '🇪🇸',
      color: '#AC4949',
      description: 'Español de España'
    }
  ];

  // Mobile menu render function - used by portal
  const renderMobileMenu = () => (
    <AnimatePresence>
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(5px)',
            zIndex: 9999, // Extremely high z-index to ensure it's on top
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0 20px'
          }}
          onClick={handleBackdropClick}
        >
          {/* Main menu */}
          {currentScreen === 'menu' && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              style={{
                width: '100%',
                maxWidth: 320,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginTop: 0, // Ensure no margin affects positioning
                marginBottom: 0
              }}
            >
<motion.div variants={cardVariants} style={{ width: '100%' }}>
  <Card 
    style={{...baseCardStyle, borderTop: '4px solid #1890ff'}}
    styles={{
      body: { 
        padding: 16
      }
    }}
    onClick={() => goToScreen('sound')}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ 
        width: 48, 
        height: 48, 
        borderRadius: 24, 
        backgroundColor: 'rgba(24, 144, 255, 0.1)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <SoundOutlined style={{ fontSize: 24, color: '#1890ff' }} />
      </div>
      <div>
        <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 4 }}>
          {t('floatingControls.soundSettings', 'Ajustes de sonido')}
        </Text>
        <Text type="secondary" style={{ fontSize: 13 }}>
                          {t('soundSettingsDesc', 'Configura el volumen de las notificaciones')}
        </Text>
      </div>
    </div>
  </Card>
</motion.div>

<motion.div variants={cardVariants} style={{ width: '100%' }}>
  <Card 
    style={{...baseCardStyle, borderTop: '4px solid #1890ff'}}
    styles={{
      body: { 
        padding: 16
      }
    }}
    onClick={() => goToScreen('language')}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ 
        width: 48, 
        height: 48, 
        borderRadius: 24, 
        backgroundColor: 'rgba(82, 196, 26, 0.1)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <GlobalOutlined style={{ fontSize: 24, color: '#52c41a' }} />
      </div>
      <div>
        <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 4 }}>
                          {t('languageSettings', 'Idioma')}
              </Text>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {t('languageSettingsDesc', 'Cambia el idioma de la aplicación')}
        </Text>
      </div>
    </div>
  </Card>
</motion.div>

              <motion.div variants={cardVariants} style={{ width: '100%' }}>
                <Card 
                  style={{...baseCardStyle, borderTop: '4px solid #ac4949'}}
styles={{
  body: { 
    padding: 16
  }
}}                  onClick={() => goToScreen('docs')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: 24, 
                      backgroundColor: 'rgba(172, 73, 73, 0.1)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <QuestionCircleOutlined style={{ fontSize: 24, color: '#ac4949' }} />
                    </div>
                    <div>
                      <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 4 }}>
                                        {t('documentation', 'Documentación')}
              </Text>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {t('documentationDesc', 'Guías y tutoriales del sistema')}
                      </Text>
                    </div>
                  </div>
                </Card>
              </motion.div>

              <motion.div variants={cardVariants} style={{ marginTop: 20 }}>
                <Button 
                  type="default" 
                  shape="round"
                  icon={<CloseOutlined />}
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    background: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                    backdropFilter: 'blur(10px)',
                    border: 'none',
                    color: theme === 'dark' ? '#fff' : '#000',
                    height: 40,
                    paddingLeft: 20,
                    paddingRight: 20
                  }}
                >
                  {t('common.close', 'Cerrar')}
                </Button>
              </motion.div>
            </motion.div>
          )}

          {/* Sound settings screen */}
          {currentScreen === 'sound' && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              style={{
                width: '100%',
                maxWidth: 350,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: theme === 'dark' ? '#1f1f1f' : '#ffffff',
                borderRadius: 16,
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ 
                padding: '16px', 
                borderBottom: `1px solid ${theme === 'dark' ? '#333' : '#f0f0f0'}`,
                display: 'flex',
                alignItems: 'center',
                gap: 12
              }}>
                <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={goBackToMenu}
                  style={{ padding: 0 }}
                />
                <Text strong style={{ fontSize: 16 }}>
                  {t('soundSettings', 'Ajustes de sonido')}
                </Text>
              </div>

              <div style={{ padding: '16px 24px', maxHeight: '80vh', overflowY: 'auto' }}>
                {soundCategories.map((category) => (
                  <motion.div 
                    key={category.key}
                    style={{ marginBottom: 20 }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ 
                      type: "spring",
                      stiffness: 500,
                      damping: 30
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                      <div style={{ 
                        width: 24, 
                        height: 24, 
                        borderRadius: 12, 
                        backgroundColor: `${category.color}20`, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        marginRight: 8
                      }}>
                        {React.cloneElement(category.icon, { style: { fontSize: 14, color: category.color } })}
                      </div>
                      <Text strong style={{ color: category.color }}>{category.label}</Text>
                    </div>
<Slider
  value={volumes[category.key as keyof typeof volumes]}
  min={0}
  max={100}
  onChange={(value) => handleVolumeChange(value, category.key)}
  marks={{ 0: '', 50: '', 100: '' }}
  tooltip={{ formatter: (value) => `${value}%` }}
  trackStyle={{ 
    backgroundColor: category.color,
    height: 6,
    borderRadius: 3
  }}
  handleStyle={{ 
    borderColor: category.color,
    boxShadow: `0 0 0 2px ${category.color}40`,
    height: 16,
    width: 16,
    marginTop: -5,
    background: category.color,
    border: 'none',
    opacity: 1,
    borderRadius: '50%',
    cursor: 'pointer'
  }}
  railStyle={{ 
    backgroundColor: theme === 'dark' ? '#333' : '#f0f0f0',
    height: 6,
    borderRadius: 3
  }}
/>

                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Language settings screen */}
          {currentScreen === 'language' && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              style={{
                width: '100%',
                maxWidth: 350,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: theme === 'dark' ? '#1f1f1f' : '#ffffff',
                borderRadius: 16,
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ 
                padding: '16px', 
                borderBottom: `1px solid ${theme === 'dark' ? '#333' : '#f0f0f0'}`,
                display: 'flex',
                alignItems: 'center',
                gap: 12
              }}>
                <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={goBackToMenu}
                  style={{ padding: 0 }}
                />
                <Text strong style={{ fontSize: 16 }}>
                  {t('languageSettings', 'Idioma')}
                </Text>
              </div>

              <div style={{ padding: '16px 24px' }}>
                <Text style={{ marginBottom: 16, display: 'block' }}>
                  {t('selectLanguage', 'Seleccione el idioma')}
                </Text>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {languageOptions.map((option) => {
                    const isSelected = (getAndSanitize('appLanguage') || 'en') === option.value;
                    
                    return (
                      <motion.div
                        key={option.value}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ 
                          type: "spring",
                          stiffness: 500,
                          damping: 30
                        }}
                      >
                        <Card
                          style={{
                            borderRadius: 8,
                            boxShadow: isSelected ? `0 0 0 2px ${option.color}, 0 4px 8px rgba(0,0,0,0.1)` : '0 2px 6px rgba(0,0,0,0.05)',
                            cursor: 'pointer',
                            borderLeft: `4px solid ${option.color}`,
                            background: isSelected 
                              ? theme === 'dark' 
                                ? `linear-gradient(to right, ${option.color}20, transparent)`
                                : `linear-gradient(to right, ${option.color}10, transparent)`
                              : undefined,
                          }}
styles={{
  body: { 
    padding: '12px 16px' 
  }
}}                          onClick={() => handleLanguageChange(option.value)}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <div style={{ fontSize: 24 }}>{option.flag}</div>
                              <div>
                                <Text strong style={{ display: 'block', fontSize: 16 }}>{option.label}</Text>
                                <Text type="secondary" style={{ fontSize: 13 }}>{option.description}</Text>
                              </div>
                            </div>
                            
                            {isSelected && (
                              <CheckOutlined style={{ color: option.color, fontSize: 18 }} />
                            )}
                          </div>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {/* Mobile or desktop controls */}
      {isMobile ? (
        <Badge dot={docButtonVibrate} offset={[-2, 2]} color="#ac4949">
          <Button
            type="text"
            icon={<SettingOutlined style={{ fontSize: '18px' }} />}
            onClick={() => setMobileMenuOpen(true)}
          />
        </Badge>
      ) : (
        <Space size={8}>
          <motion.div
            animate={animateSoundButton ? {
              scale: [1, 1.2, 1],
              rotate: [0, 5, -5, 0]
            } : {}}
            transition={animateSoundButton ? { duration: 0.5 } : {}}
          >
            <Button
              type="text"
              icon={<SoundOutlined style={{ fontSize: '16px', color: '#8b8b8b' }} />}
              onClick={() => setSoundOpen(true)}
            />
          </motion.div>
          <Button
            type="text"
            icon={<GlobalOutlined style={{ fontSize: '16px', color: '#8b8b8b' }} />}
            onClick={() => setLanguageOpen(true)}
          />
          <Badge dot={docButtonVibrate} offset={[-2, 2]} color="#ac4949">
            <motion.div
              animate={docButtonVibrate ? {
                x: [-1, 1, -1, 1, -2, 2, -2, 2, -1, 1, -1, 1],
                rotate: [-0.5, 0.5, -0.5, 0.5, -1, 1, -1, 1, -0.5, 0.5, -0.5, 0.5],
              } : undefined}
              transition={docButtonVibrate ? {
                duration: 0.5,
                repeat: 3,
                repeatType: "reverse"
              } : undefined}
            >
              <Button
                type="text"
                icon={<QuestionCircleOutlined style={{ fontSize: '16px', color: docButtonVibrate ? '#ac4949' : '#8b8b8b' }} />}
                onClick={() => setDocsOpen(true)}
              />
            </motion.div>
          </Badge>
        </Space>
      )}

      {/* Render the mobile menu using a portal to avoid inheritance issues */}
      <MobileMenuPortal isOpen={isMobile}>
        {renderMobileMenu()}
      </MobileMenuPortal>

      {/* Enhanced Sound Settings Drawer */}
      <Drawer
        title={
          <Space>
            <Avatar 
              icon={<SoundFilled />} 
              style={{ 
                backgroundColor: '#1890ff', 
                backgroundImage: 'linear-gradient(45deg, #1890ff, #52c41a)', 
                boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)' 
              }} 
            />
            <span style={{ fontSize: '18px', fontWeight: 500 }}>
              {t('soundSettings', 'Ajustes de sonido')}
            </span>
          </Space>
        }
        placement="right"
        onClose={() => setSoundOpen(false)}
        open={!isMobile && soundOpen}
        width={360}
        styles={{
          header: {
            padding: '16px 24px',
            borderBottom: `1px solid ${theme === 'dark' ? '#333' : '#f0f0f0'}`,
            background: theme === 'dark' 
              ? 'linear-gradient(to right, #141414, #1f1f1f)' 
              : 'linear-gradient(to right, #f9f9f9, #ffffff)'
          },
          body: {
            padding: '16px',
            overflowX: 'hidden'
          }
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card
            style={{
              marginBottom: 20,
              borderRadius: 12,
              background: theme === 'dark' 
                ? 'linear-gradient(145deg, #141414, #1a1a1a)' 
                : 'linear-gradient(145deg, #f9f9f9, #ffffff)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              overflow: 'hidden',
              border: 'none'
            }}
          >
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              borderBottom: `1px solid ${theme === 'dark' ? '#333' : '#f0f0f0'}`,
              padding: '0 0 12px 0',
              marginBottom: '16px'
            }}>
              <Title level={5} style={{ margin: 0 }}>
                {t('volumeTitle', 'Volumen de notificaciones')}
              </Title>
              <motion.div
                animate={animateSoundButton ? {
                  scale: [1, 1.2, 1],
                  rotate: [0, 5, -5, 0]
                } : {}}
                transition={animateSoundButton ? { duration: 0.5 } : {}}
              >
                <SoundFilled style={{ 
                  fontSize: 20, 
                  color: theme === 'dark' ? '#1890ff' : '#1890ff' 
                }} />
              </motion.div>
            </div>

            {soundCategories.map((category, index) => (
              <motion.div 
                key={category.key}
                style={{ marginBottom: index === soundCategories.length - 1 ? 0 : 24 }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ 
                  delay: index * 0.08,
                  type: "spring",
                  stiffness: 500,
                  damping: 30
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: 8,
                  gap: 8
                }}>
                  <div style={{ 
                    width: 30, 
                    height: 30, 
                    borderRadius: 15, 
                    backgroundColor: `${category.color}20`, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(category.icon, { style: { fontSize: 16, color: category.color } })}
                  </div>
                  <Text strong style={{ color: category.color }}>{category.label}</Text>
                </div>
                
                <div style={{ padding: '0 5px' }}>
<Slider
  value={volumes[category.key as keyof typeof volumes]}
  min={0}
  max={100}
  onChange={(value) => handleVolumeChange(value, category.key)}
  marks={{ 0: '', 50: '', 100: '' }}
  tooltip={{ formatter: (value) => `${value}%` }}
  trackStyle={{ 
    backgroundColor: category.color,
    height: 6,
    borderRadius: 3
  }}
  handleStyle={{ 
    borderColor: category.color,
    boxShadow: `0 0 0 2px ${category.color}40`,
    height: 16,
    width: 16,
    marginTop: -7,
    background: category.color,
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer'
  }}
  railStyle={{ 
    backgroundColor: theme === 'dark' ? '#333' : '#f0f0f0',
    height: 6,
    borderRadius: 3
  }}
/>

                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    marginTop: 4,
                    fontSize: 12,
                    color: theme === 'dark' ? '#999' : '#999'
                  }}>
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </Card>
        </motion.div>
      </Drawer>

      {/* Enhanced Language Settings Drawer */}
      <Drawer
        title={
          <Space>
            <Avatar 
              icon={<GlobalOutlined />} 
              style={{ 
                backgroundColor: '#52c41a', 
                backgroundImage: 'linear-gradient(45deg, #52c41a, #13c2c2)',
                boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)' 
              }} 
            />
            <span style={{ fontSize: '18px', fontWeight: 500 }}>
              {t('languageSettings', 'Cambiar idioma')}
            </span>
          </Space>
        }
        placement="right"
        onClose={() => setLanguageOpen(false)}
        open={!isMobile && languageOpen}
        width={360}
        styles={{
          header: {
            padding: '16px 24px',
            borderBottom: `1px solid ${theme === 'dark' ? '#333' : '#f0f0f0'}`,
            background: theme === 'dark' 
              ? 'linear-gradient(to right, #141414, #1f1f1f)' 
              : 'linear-gradient(to right, #f9f9f9, #ffffff)'
          },
          body: {
            padding: '16px',
            overflowX: 'hidden'
          }
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card
            style={{
              marginBottom: 20,
              borderRadius: 12,
              background: theme === 'dark' 
                ? 'linear-gradient(145deg, #141414, #1a1a1a)' 
                : 'linear-gradient(145deg, #f9f9f9, #ffffff)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              overflow: 'hidden',
              border: 'none'
            }}
          >
            <div style={{ 
              padding: '8px 16px 16px',
              borderBottom: `1px solid ${theme === 'dark' ? '#333' : '#f0f0f0'}`,
              marginBottom: 16
            }}>
              <Text style={{ 
                fontSize: 16, 
                display: 'block', 
                marginBottom: 8,
                color: theme === 'dark' ? '#e6e6e6' : '#333'
              }}>
                {t('selectLanguage', 'Seleccione el idioma')}
              </Text>
              <Text type="secondary" style={{ display: 'block', fontSize: 14 }}>
                {t('languageInfo', 'El cambio de idioma se aplicará inmediatamente a toda la aplicación.')}
              </Text>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '0 8px' }}>
              {languageOptions.map((option, index) => {
                const isSelected = (getAndSanitize('appLanguage') || 'en') === option.value;
                
                return (
                  <motion.div
                    key={option.value}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ 
                      delay: index * 0.1,
                      type: "spring",
                      stiffness: 500,
                      damping: 30
                    }}
                  >
                    <Card
                      style={{
                        borderRadius: 12,
                        boxShadow: isSelected ? `0 0 0 2px ${option.color}, 0 4px 12px rgba(0,0,0,0.1)` 
                          : '0 2px 8px rgba(0,0,0,0.05)',
                        cursor: 'pointer',
                        borderLeft: `5px solid ${option.color}`,
                        background: isSelected 
                          ? theme === 'dark' 
                            ? `linear-gradient(to right, ${option.color}20, transparent)`
                            : `linear-gradient(to right, ${option.color}10, transparent)`
                          : theme === 'dark' ? '#1f1f1f' : '#ffffff',
                        transition: 'all 0.3s ease'
                      }}
                      styles={{
                        body: { 
                          padding: 16
                        }
                      }}  
                      onClick={() => handleLanguageChange(option.value)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                          <div style={{ 
                            fontSize: 32, 
                            width: 48, 
                            height: 48, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            background: `${option.color}10`,
                            borderRadius: 8
                          }}>
                            {option.flag}
                          </div>
                          <div>
                            <Text strong style={{ 
                              display: 'block', 
                              fontSize: 18,
                              color: theme === 'dark' ? '#e6e6e6' : '#333'
                            }}>
                              {option.label}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 14 }}>{option.description}</Text>
                          </div>
                        </div>
                        
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ 
                              type: "spring", 
                              stiffness: 500, 
                              damping: 30 
                            }}
                          >
                            <CheckOutlined style={{ 
                              color: option.color, 
                              fontSize: 18,
                              background: `${option.color}20`,
                              padding: 8,
                              borderRadius: 20,
                              width: 36,
                              height: 36,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }} />
                          </motion.div>
                        )}
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            <div style={{ 
              marginTop: 20, 
              padding: '16px',
              borderTop: `1px solid ${theme === 'dark' ? '#333' : '#f0f0f0'}`,
              display: 'flex',
              justifyContent: 'center'
            }}>
              <Text type="secondary" style={{ fontSize: 13, textAlign: 'center' }}>
                {t('moreLanguages', 'Más idiomas estarán disponibles próximamente')}
              </Text>
            </div>
          </Card>
        </motion.div>
      </Drawer>

      {/* Documentation Component */}
      <Documentation 
        open={docsOpen} 
        onClose={() => setDocsOpen(false)} 
      />

    </>
  );
};

export default HeaderControls;